import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { isLightColor, getContrastTextColor } from '../../utils/colorContrast';

const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.isCompact ? '4px 12px' : '8px 12px 8px 16px'};
  gap: 8px;
  height: ${props => props.isCompact ? '32px' : '40px'};
  min-width: ${props => (props.isText ? 'auto' : props.isCompact ? '100px' : '128px')};
  border: ${props => props.outlinedBorder || 'none'};
  background: ${props => props.containedBg};
  border-radius: 20px;
  cursor: pointer;
  text-transform: none;
  box-shadow: none;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  font-family: inherit;

  .label {
    margin-top: 1px;
    font-size: ${props => props.isCompact ? '14px' : '16px'};
    line-height: ${props => props.isCompact ? '16px' : '20px'};
    color: ${props => props.labelColor};
    letter-spacing: ${props => props.isCompact ? '0.5px' : '1px'};
    font-weight: normal;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover:not(:disabled) {
    background: ${props => props.containedBgHover};
    box-shadow: none;
  }

  &:disabled {
    background: ${props => props.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    color: ${props => props.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid ${props => props.focusColor};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const buildButtonColors = ({
  isDarkMode,
  variant,
  isSecondary,
  buttonColor,
  secondaryColor,
  explicitAccent,
  isLightSecondary,
}) => {
  const tint = isDarkMode ? '40' : '27';
  const tintHover = isDarkMode ? '58' : '49';
  const focusColor = buttonColor || '#056dfa';

  // ponytail: text = transparent chip, not a separate component
  if (variant === 'text') {
    const accent = explicitAccent ? buttonColor : (isSecondary ? secondaryColor : buttonColor);
    const labelColor = isSecondary
      ? (isDarkMode
        ? (isLightSecondary ? secondaryColor : '#E4E6EB')
        : (isLightSecondary ? '#333333' : secondaryColor))
      : (isDarkMode && !isLightColor(accent) && accent !== '#13FFA3'
        ? '#E4E6EB'
        : accent);
    return {
      containedBg: 'transparent',
      containedBgHover: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : `${accent}14`,
      labelColor,
      outlinedBorder: 'none',
      focusColor: accent,
    };
  }

  if (variant === 'outlined') {
    const accent = explicitAccent ? buttonColor : (isSecondary ? secondaryColor : buttonColor);
    const borderColor = isSecondary && isDarkMode && !isLightSecondary
      ? 'rgba(255, 255, 255, 0.28)'
      : accent;
    const labelColor = isSecondary
      ? (isDarkMode
        ? (isLightSecondary ? secondaryColor : '#E4E6EB')
        : (isLightSecondary ? '#333333' : secondaryColor))
      : (isDarkMode && !isLightColor(accent) && accent !== '#13FFA3'
        ? '#E4E6EB'
        : accent);
    return {
      containedBg: 'transparent',
      containedBgHover: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : `${accent}10`,
      labelColor,
      outlinedBorder: `1px solid ${borderColor}`,
      focusColor,
    };
  }

  if (isSecondary) {
    const labelColor = isDarkMode
      ? (isLightSecondary ? secondaryColor : '#E4E6EB')
      : (isLightSecondary ? '#333333' : secondaryColor);
    const bg = isDarkMode && !isLightSecondary
      ? 'rgba(255, 255, 255, 0.1)'
      : `${secondaryColor}${tint}`;
    const bgHover = isDarkMode && !isLightSecondary
      ? 'rgba(255, 255, 255, 0.16)'
      : `${secondaryColor}${tintHover}`;
    return {
      containedBg: bg,
      containedBgHover: bgHover,
      labelColor,
      outlinedBorder: 'none',
      focusColor: secondaryColor,
    };
  }

  if (isDarkMode) {
    if (buttonColor === '#13FFA3') {
      return {
        containedBg: `${buttonColor}${tint}`,
        containedBgHover: `${buttonColor}${tintHover}`,
        labelColor: '#000000',
        outlinedBorder: 'none',
        focusColor,
      };
    }
    if (!isLightColor(buttonColor)) {
      return {
        containedBg: buttonColor,
        containedBgHover: `${buttonColor}dd`,
        labelColor: getContrastTextColor(buttonColor),
        outlinedBorder: 'none',
        focusColor: buttonColor,
      };
    }
    return {
      containedBg: `${buttonColor}${tint}`,
      containedBgHover: `${buttonColor}${tintHover}`,
      labelColor: buttonColor,
      outlinedBorder: 'none',
      focusColor,
    };
  }

  const labelColor = buttonColor === '#13FFA3' ? '#000000' : buttonColor;
  return {
    containedBg: `${buttonColor}${tint}`,
    containedBgHover: `${buttonColor}${tintHover}`,
    labelColor,
    outlinedBorder: 'none',
    focusColor,
  };
};

const NewCustomButton = React.forwardRef(({
  children,
  color,
  customColor, // ponytail: alias for color — call sites historically used customColor
  startIcon,
  endIcon,
  onClick,
  disabled = false,
  loading = false,
  sx = {},
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  type = 'button',
  compact = false,
  isCompact = false, // ponytail: alias for compact
  ...props
}, ref) => {
  const { isDarkMode, primaryColor, secondaryColor } = useTheme();

  const isSecondary = variant === 'secondary';
  const buttonColor = color || customColor || primaryColor || '#056dfa';
  const explicitAccent = Boolean(color || customColor);
  const isLightSecondary = isLightColor(secondaryColor);
  const compactMode = compact || isCompact || size === 'small';
  const isText = variant === 'text';

  const buttonColors = useMemo(
    () => buildButtonColors({
      isDarkMode,
      variant,
      isSecondary,
      buttonColor,
      secondaryColor,
      explicitAccent,
      isLightSecondary,
    }),
    [isDarkMode, variant, isSecondary, buttonColor, secondaryColor, explicitAccent, isLightSecondary]
  );

  const spinnerColor = explicitAccent ? buttonColor : (isSecondary ? secondaryColor : buttonColor);

  const customStyles = {
    ...(fullWidth && { width: '100%' }),
    ...sx,
  };

  return (
    <StyledButton
      ref={ref}
      {...props}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      isDarkMode={isDarkMode}
      isCompact={compactMode}
      isText={isText}
      containedBg={buttonColors.containedBg}
      containedBgHover={buttonColors.containedBgHover}
      labelColor={buttonColors.labelColor}
      outlinedBorder={buttonColors.outlinedBorder}
      focusColor={buttonColors.focusColor}
      style={customStyles}
    >
      {loading ? (
        <CircularProgress
          size={compactMode ? 12 : 16}
          thickness={5}
          sx={{ color: spinnerColor, flexShrink: 0 }}
        />
      ) : (
        startIcon && <span className="start-icon">{startIcon}</span>
      )}
      <span className="label">{children}</span>
      {!loading && endIcon && <span className="end-icon">{endIcon}</span>}
    </StyledButton>
  );
});

NewCustomButton.displayName = 'NewCustomButton';

export default NewCustomButton;
